import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Camera, Menu, ShoppingBag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CartDrawer } from "@/components/CartDrawer";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";

const navItems = [
  { label: "Events", href: "/events", testId: "nav-link-events" },
  { label: "Search", href: "/search", testId: "nav-link-search" },
  { label: "Manifesto", href: "/about", testId: "nav-link-manifesto" },
  { label: "Licensing", href: "/licensing", testId: "nav-link-licensing" },
  { label: "Contact", href: "/contact", testId: "nav-link-contact" },
];

export function Layout() {
  const { items, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="grain min-h-screen bg-onyx text-white">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/72 backdrop-blur-2xl">
        <div className="mx-auto flex h-20 max-w-[1600px] items-center justify-between px-4 sm:px-8 lg:px-12">
          <Link data-testid="nav-brand-logo" to="/" className="group flex items-center gap-3" onClick={() => setMobileOpen(false)}>
            <span className="flex h-10 w-10 items-center justify-center border border-gold/40 bg-gold/10 text-gold transition-colors group-hover:bg-gold group-hover:text-onyx">
              <Camera className="h-4 w-4" />
            </span>
            <span className="font-lux text-sm font-semibold uppercase tracking-[0.34em] text-white">mellowjmedia</span>
          </Link>
          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary navigation">
            {navItems.map((item) => (
              <NavLink key={item.href} data-testid={item.testId} to={item.href} className={({ isActive }) => `font-mono text-[11px] uppercase tracking-[0.28em] transition-colors ${isActive ? "text-gold" : "text-zinc-400 hover:text-white"}`}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-3 lg:flex">
            {user?.role === "admin" && <Link data-testid="nav-link-admin" className="font-mono text-[11px] uppercase tracking-[0.28em] text-zinc-400 hover:text-white" to="/admin">Admin</Link>}
            {user && <Link data-testid="nav-link-dashboard" className="font-mono text-[11px] uppercase tracking-[0.28em] text-zinc-400 hover:text-white" to="/dashboard">Downloads</Link>}
            {user ? (
              <button data-testid="nav-logout-button" onClick={logout} className="font-mono text-[11px] uppercase tracking-[0.28em] text-zinc-400 hover:text-white">Logout</button>
            ) : (
              <Link data-testid="nav-link-account" className="font-mono text-[11px] uppercase tracking-[0.28em] text-zinc-400 hover:text-white" to="/account">Account</Link>
            )}
            <Button data-testid="nav-link-cart" onClick={() => setOpen(true)} variant="outline" className="relative h-11 rounded-none border-gold/40 bg-transparent px-5 font-mono text-[11px] uppercase tracking-[0.24em] text-gold hover:bg-gold hover:text-onyx">
              <ShoppingBag className="h-4 w-4" /> Cart
              {items.length > 0 && <span data-testid="cart-count-badge" className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-gold text-[10px] text-onyx">{items.length}</span>}
            </Button>
          </div>
          <button data-testid="mobile-menu-button" className="p-2 text-white lg:hidden" onClick={() => setMobileOpen((value) => !value)} aria-label="Toggle menu">
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {mobileOpen && (
          <div data-testid="mobile-navigation-menu" className="border-t border-white/10 bg-onyx px-4 py-6 lg:hidden">
            <div className="flex flex-col gap-5">
              {navItems.map((item) => <Link key={item.href} data-testid={`mobile-${item.testId}`} to={item.href} onClick={() => setMobileOpen(false)} className="font-mono text-xs uppercase tracking-[0.28em] text-zinc-300">{item.label}</Link>)}
              <Link data-testid="mobile-nav-link-cart" to={location.pathname} onClick={() => { setOpen(true); setMobileOpen(false); }} className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Cart ({items.length})</Link>
              <Link data-testid="mobile-nav-link-account" to={user ? "/dashboard" : "/account"} onClick={() => setMobileOpen(false)} className="font-mono text-xs uppercase tracking-[0.28em] text-zinc-300">{user ? "Downloads" : "Account"}</Link>
            </div>
          </div>
        )}
      </header>
      <main>
        <Outlet />
      </main>
      <footer className="border-t border-gold/15 bg-black px-4 py-14 sm:px-8 lg:px-12">
        <div className="mx-auto grid max-w-[1600px] gap-10 md:grid-cols-[1.3fr_1fr_1fr]">
          <div>
            <p className="font-display text-3xl text-white">mellowjmedia</p>
            <p className="mt-4 max-w-md text-sm leading-7 text-zinc-500">Cinematic digital photography for motorcycle, automotive and mixed-machine events. Every download is delivered in high resolution after secure checkout.</p>
          </div>
          <div>
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-gold">Library</p>
            <div className="grid gap-3 text-sm text-zinc-500">
              <Link data-testid="footer-link-events" to="/events" className="hover:text-white">Events library</Link>
              <Link data-testid="footer-link-riders" to="/riders" className="hover:text-white">Riders & drivers</Link>
              <Link data-testid="footer-link-faq" to="/faq" className="hover:text-white">FAQ</Link>
              <Link data-testid="footer-link-contact" to="/contact" className="hover:text-white">Contact</Link>
            </div>
          </div>
          <div>
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-gold">Legal</p>
            <div className="grid gap-3 text-sm text-zinc-500">
              <Link data-testid="footer-link-licensing" to="/licensing" className="hover:text-white">Licensing</Link>
              <Link data-testid="footer-link-terms" to="/terms" className="hover:text-white">Terms</Link>
              <Link data-testid="footer-link-privacy" to="/privacy" className="hover:text-white">Privacy</Link>
              <Link data-testid="footer-link-refunds" to="/refunds" className="hover:text-white">Refunds</Link>
            </div>
          </div>
        </div>
        <div className="mx-auto mt-12 flex max-w-[1600px] flex-col gap-3 border-t border-white/10 pt-6 font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-600 sm:flex-row sm:items-center sm:justify-between">
          <span>© 2026 mellowjmedia</span>
          <span>Darkroom / Digital / Delivered</span>
        </div>
      </footer>
      <CartDrawer />
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}
